import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { ExpensablesDataTableQuery } from 'generated/resources/datatable.graphql'

import DataTable, { useDataTable } from 'modules/core/components/DataTable'
import { DateNavigator } from 'modules/core/components/DateNavigator'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { Options } from 'modules/expenses/components/expenses/DataTable/_Actions/Options'
import type { Expensable } from 'modules/expenses/components/expenses/DataTable/types'
import { ActionFn } from 'modules/expenses/components/expenses/DataTable/useActions'
import { PageDateFilters, usePageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import {
  canAddExpensableToGroup,
  canAddExpensableToPayment,
  canAddExpensableToPayroll,
  canRemoveExpensableFromGroup,
  canRemoveExpensableFromPayroll,
  canReviewExpensable,
} from 'modules/expenses/lib/expensables/availableActions'
import {
  everyReimbursableCurrencyAllowed,
  showNotAllowedCurrenciesMessage,
} from 'modules/expenses/lib/expensables/expensable'
import { useMyExpenses } from 'modules/expenses/pages/my_expenses/MyExpensesContext'
import { usePageActions } from 'modules/expenses/pages/my_expenses/usePageActions'

interface HeaderProps {
  search: string
  onApprove: ActionFn
  onReject: ActionFn
  onRestartReview: ActionFn
  onAddToPayroll: ActionFn
  onRemoveFromPayroll: ActionFn
  onSetToPaid: ActionFn
  onRevertPaid: ActionFn
  onAddToGroup: ActionFn
  onAddToPayment: ActionFn
  onRemoveFromGroup: ActionFn
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
  dateFilters: PageDateFilters
}

export const Header = ({
  onApprove,
  onReject,
  onAddToPayroll,
  onAddToPayment,
  onRemoveFromPayroll,
  onAddToGroup,
  onRemoveFromGroup,
  dateFilters,
}: HeaderProps) => {
  const navigate = useNavigate()
  const { employee } = useCurrentSession()
  const { data, selectedRows } = useDataTable<ExpensablesDataTableQuery, Expensable>()
  const { featureAvailabilities } = useMyExpenses()
  const { setPageDateFilters } = usePageDateFilters()
  const selectedIds = [...selectedRows].map(Number)
  const selectedExpensables = data.filter(({ id }) => selectedIds.includes(id))

  const availableActions = {
    canReview: selectedExpensables.some((expensable) =>
      canReviewExpensable(expensable, 'personal')
    ),
    canAddToGroup: selectedExpensables.some((expensable) =>
      canAddExpensableToGroup(
        featureAvailabilities.expensableGroups,
        expensable,
        'personal',
        employee
      )
    ),
    canRemoveFromGroup: selectedExpensables.some((expensable) =>
      canRemoveExpensableFromGroup(expensable, 'personal')
    ),
    canRemoveFromPayroll: selectedExpensables.some((expensable) =>
      canRemoveExpensableFromPayroll(expensable, featureAvailabilities.reimburseThroughPayroll)
    ),
    canAddToPayroll: selectedExpensables.some((expensable) =>
      canAddExpensableToPayroll(expensable, featureAvailabilities.reimburseThroughPayroll)
    ),
    canAddToPayment: selectedExpensables.some((expensable) =>
      canAddExpensableToPayment(expensable, featureAvailabilities.reimburseThroughSepa)
    ),
  }

  const handleApprove = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canReviewExpensable(expensable, 'personal')
    )

    onApprove(validExpensables)
  }

  const handleReject = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canReviewExpensable(expensable, 'personal')
    )

    onReject(validExpensables)
  }

  const handleRemoveFromPayroll = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canRemoveExpensableFromPayroll(expensable, featureAvailabilities.reimburseThroughPayroll)
    )

    onRemoveFromPayroll(validExpensables)
  }

  const handleAddToPayroll = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canAddExpensableToPayroll(expensable, featureAvailabilities.reimburseThroughPayroll)
    )

    if (!everyReimbursableCurrencyAllowed(validExpensables)) {
      return showNotAllowedCurrenciesMessage()
    }

    onAddToPayroll(validExpensables)
  }

  const handleAddToPayment = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canAddExpensableToPayment(expensable, featureAvailabilities.reimburseThroughSepa)
    )

    onAddToPayment(validExpensables)
  }

  const handleAddToGroup = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canAddExpensableToGroup(
        featureAvailabilities.expensableGroups,
        expensable,
        'personal',
        employee
      )
    )

    onAddToGroup(validExpensables)
  }

  const handleRemoveFromGroup = () => {
    const validExpensables = selectedExpensables.filter((expensable) =>
      canRemoveExpensableFromGroup(expensable, 'personal')
    )

    onRemoveFromGroup(validExpensables)
  }
  const { actions } = usePageActions({
    isInMyExpensesSection: true,
    canUsePerDiems: featureAvailabilities.canUsePerDiems,
    canCreateExpenses: featureAvailabilities.canCreateExpenses,
    expensableGroupsEnabled: featureAvailabilities.expensableGroups,
  })
  const { primary, secondary } = actions

  return (
    <Box gap="s16">
      <DataTable.HeadBar
        disableExport
        customPopoverOptions={
          secondary &&
          secondary.map((secondaryAction) => (
            <PopoverMenu.RouterOption
              key={secondaryAction.key}
              title={secondaryAction.title}
              to={secondaryAction.to}
            />
          ))
        }
        actions={
          <>
            <DateNavigator
              startDate={dateFilters.startDate}
              endDate={dateFilters.endDate}
              onDateRangeChange={setPageDateFilters}
            />
            {selectedExpensables.length > 0 && (
              <PopoverMenu type="list">
                <PopoverMenu.Trigger>
                  <Button.Decorative type="secondary">{i18n.t('common.actions')}</Button.Decorative>
                </PopoverMenu.Trigger>
                <PopoverMenu.Content side="bottom" align="end">
                  <Options
                    {...availableActions}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onRemoveFromPayroll={handleRemoveFromPayroll}
                    onAddToPayroll={handleAddToPayroll}
                    onAddToGroup={handleAddToGroup}
                    onAddToPayment={handleAddToPayment}
                    onRemoveFromGroup={handleRemoveFromGroup}
                  />
                </PopoverMenu.Content>
              </PopoverMenu>
            )}
            {primary && primary.length > 0 && (
              <PopoverMenu type="list">
                <PopoverMenu.Trigger>
                  <Button.Decorative type="primary" iconName="add">
                    {i18n.t('command_bar.plugins.actions.my_expenses.add_expense')}
                  </Button.Decorative>
                </PopoverMenu.Trigger>
                <PopoverMenu.Content side="bottom" align="center">
                  {primary.map((action) => (
                    <PopoverMenu.TouchableOption
                      title={action.label}
                      key={action.key}
                      onClick={() => navigate(action.to)}
                    />
                  ))}
                </PopoverMenu.Content>
              </PopoverMenu>
            )}
          </>
        }
      />
    </Box>
  )
}
